import { useCallback, useRef, useState, type ReactElement } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  ActionFeedbackStatusSchema,
  type FeedbackAction,
  type HappinessRatingValue,
} from '@taqon/contracts';
import { getData, putJson } from '../../shared/api/client';
import { useSession } from '../../shared/session/session';
import { FACES } from './faces';

const BASE = '/api/v1/happiness/action-feedback';
/** Each action's address under the feedback API (one route each, SDD §31.8). */
const PATHS: Record<FeedbackAction, string> = {
  leave_request: 'leave-requests',
  employee_request: 'employee-requests',
  purchase_request: 'purchase-requests',
  expense_claim: 'expense-claims',
};
/** What the person just did, as the question names it. */
export const FEEDBACK_ACTION_LABEL: Record<FeedbackAction, string> = {
  leave_request: 'submitting a leave request',
  employee_request: 'submitting an employee request',
  purchase_request: 'submitting a purchase request',
  expense_claim: 'submitting an expense claim',
};
const COMMENT_LIMIT = 500;

/**
 * The offer to rate an action just done (FRD-CORE-062, CORE-063; CLR-61). `offer(action)` asks the
 * API whether today's offer for that action is still open and, if it is, opens the dialog; `dialog`
 * is rendered by the page. "Not now", Escape or closing records that the offer was declined, so it
 * is not made again that day. Feedback must never get in the way: if the API cannot be reached or
 * refuses, nothing is shown.
 */
export function useActionFeedback(): {
  offer: (action: FeedbackAction) => void;
  dialog: ReactElement | null;
} {
  const [open, setOpen] = useState<FeedbackAction | null>(null);
  // An action is offered at most once while the page is open (a re-render or a remount in
  // development would otherwise ask twice).
  const offered = useRef(new Set<FeedbackAction>());

  const offer = useCallback((action: FeedbackAction) => {
    if (offered.current.has(action)) return;
    offered.current.add(action);
    void getData(`${BASE}/${PATHS[action]}`, ActionFeedbackStatusSchema)
      .then((status) => {
        if (!status.answered) setOpen(action);
      })
      .catch(() => undefined);
  }, []);

  return {
    offer,
    dialog:
      open === null ? null : (
        <FeedbackDialog
          key={open}
          action={open}
          onDone={() => {
            setOpen(null);
          }}
        />
      ),
  };
}

function FeedbackDialog({ action, onDone }: { action: FeedbackAction; onDone: () => void }) {
  const session = useSession();
  const [rating, setRating] = useState<HappinessRatingValue | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const trimmed = comment.trim();

  const send = (answer: { rating: HappinessRatingValue | null; comment: string | null }) => {
    setSending(true);
    void putJson(
      `${BASE}/${PATHS[action]}`,
      answer,
      ActionFeedbackStatusSchema,
      session.data?.csrfToken ?? '',
    )
      // Feedback is optional: a failed answer is not worth an error in the person's way.
      .catch(() => undefined)
      .finally(onDone);
  };
  const decline = () => {
    if (!sending) send({ rating: null, comment: null });
  };

  return (
    <Dialog open onClose={decline} fullWidth maxWidth="xs" aria-labelledby="feedback-title">
      <DialogTitle id="feedback-title">How was {FEEDBACK_ACTION_LABEL[action]}?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Your answer is anonymous: the results show counts and comments, never names. You are
            asked once a day.
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={rating}
            aria-label="Your rating"
            onChange={(_event, next: HappinessRatingValue | null) => {
              setRating(next);
            }}
          >
            {FACES.map((face) => (
              <ToggleButton
                key={face.value}
                value={face.value}
                color={face.color}
                sx={{ flexDirection: 'column', gap: 0.5, py: 1.5 }}
              >
                {face.icon}
                {face.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            label="Comment (optional)"
            multiline
            minRows={2}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: COMMENT_LIMIT } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={decline} disabled={sending}>
          Not now
        </Button>
        <Button
          variant="contained"
          disabled={rating === null || sending}
          onClick={() => {
            if (rating !== null) send({ rating, comment: trimmed === '' ? null : trimmed });
          }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
